import { useEffect, useState } from 'react';
import OBR from '@owlbear-rodeo/sdk';
import { bridgeRequest, type BridgeRoomState } from '../obr/gmBridgeClient.ts';
import { clearBridgeKey, generateBridgeKey, readBridgeKey, writeBridgeKey } from '../obr/gmBridgeConfig.ts';
import { isDicePlusReady } from '../obr/dicePlus.ts';

function validRoomState(value: BridgeRoomState | null | undefined): value is BridgeRoomState {
  return !!value
    && Number.isInteger(value.floor) && value.floor >= 1 && value.floor <= 18
    && Number.isInteger(value.version) && value.version >= 1
    && typeof value.createdAt === 'number'
    && typeof value.updatedAt === 'number'
    && typeof value.updatedBy === 'string';
}

export function GmBridgePanel() {
  const roomId = OBR.room.id;
  const [key, setKey] = useState(() => readBridgeKey(roomId));
  const [status, setStatus] = useState(() => readBridgeKey(roomId) ? 'Bridge secret is stored on this browser.' : 'Bridge not configured.');
  const [floorState, setFloorState] = useState<BridgeRoomState | null>(null);
  const [floorDraft, setFloorDraft] = useState(1);
  const [floorStatus, setFloorStatus] = useState('');
  const [floorBusy, setFloorBusy] = useState(false);

  async function loadFloor(activeKey = key) {
    if (!activeKey) {
      setFloorState(null);
      setFloorStatus('Configure the GM bridge below before setting the campaign Floor.');
      return;
    }
    setFloorBusy(true);
    const result = await bridgeRequest(activeKey, roomId, { action: 'roomState' });
    setFloorBusy(false);
    if (!result.ok) {
      setFloorStatus(`Could not read campaign Floor: ${result.error ?? 'unknown error'}`);
      return;
    }
    if (result.roomState == null) {
      setFloorState(null);
      setFloorStatus('No master Floor has been set yet. Choose the current Floor and save it once.');
      return;
    }
    if (!validRoomState(result.roomState)) {
      setFloorStatus('The server returned unreadable campaign Floor data.');
      return;
    }
    setFloorState(result.roomState);
    setFloorDraft(result.roomState.floor);
    setFloorStatus('This Floor is the master value used by server-backed crawler sheets.');
  }

  useEffect(() => { void loadFloor(key); }, [key, roomId]);

  async function saveFloor() {
    if (!key) { setFloorStatus('Configure the GM bridge below first.'); return; }
    if (!Number.isInteger(floorDraft) || floorDraft < 1 || floorDraft > 18) {
      setFloorStatus('Floor must be a whole number from 1 to 18.');
      return;
    }
    setFloorBusy(true);
    const result = await bridgeRequest(key, roomId, {
      action: 'setFloor',
      floor: floorDraft,
      expectedVersion: floorState?.version ?? 0,
    });
    setFloorBusy(false);
    if (!result.ok || !validRoomState(result.roomState)) {
      setFloorStatus(result.error ?? 'The Floor was not saved. Refresh the Floor and try again.');
      return;
    }
    setFloorState(result.roomState);
    setFloorDraft(result.roomState.floor);
    setFloorStatus(`Floor ${result.roomState.floor} saved as the campaign master Floor.`);
  }

  async function generate() {
    const next = generateBridgeKey();
    if (!writeBridgeKey(roomId, next)) {
      setStatus('This browser blocked local storage, so the bridge secret could not be saved.');
      return;
    }
    setKey(next);
    setStatus('New secret created locally. Copy it into Cloudflare as DCC_BRIDGE_SECRET and into the Obsidian GM Console.');
  }

  async function copy() {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      setStatus('Bridge secret copied to clipboard.');
    } catch {
      setStatus('Clipboard access was blocked. Select the key and copy it manually.');
    }
  }

  async function testServer() {
    if (!key) { setStatus('Generate a bridge secret first.'); return; }
    setStatus('Testing Cloudflare bridge…');
    const result = await bridgeRequest(key, roomId, { action: 'ping' });
    setStatus(result.ok ? 'Cloudflare bridge is ready.' : `Bridge error: ${result.error ?? 'unknown error'}`);
    if (result.ok) await loadFloor(key);
  }

  async function testDice() {
    setStatus('Checking Dice+…');
    const ready = await isDicePlusReady();
    setStatus(ready ? 'Dice+ is ready in this room.' : 'Dice+ did not answer. Confirm Dice+ is enabled in this room.');
  }

  function clear() {
    if (!window.confirm('Clear the saved GM bridge secret from this browser? Obsidian integration will stop until it is restored.')) return;
    if (!clearBridgeKey(roomId)) { setStatus('This browser blocked local storage, so the key could not be cleared.'); return; }
    setKey('');
    setStatus('Bridge secret cleared on this browser.');
  }

  return (
    <>
      <section className="dcc-floor-panel" aria-label="campaign floor control">
        <div className="dcc-floor-heading">
          <div>
            <strong>Campaign Floor</strong>
            <p>{floorState ? `Master Floor: ${floorState.floor}` : 'Master Floor: not set'}</p>
          </div>
          <button disabled={floorBusy || !key} onClick={() => void loadFloor()}>{floorBusy ? 'Checking…' : 'Refresh'}</button>
        </div>
        <div className="dcc-floor-controls">
          <label>Floor
            <input
              type="number"
              min={1}
              max={18}
              value={floorDraft}
              disabled={floorBusy || !key}
              onChange={(event) => setFloorDraft(Number(event.target.value))}
            />
          </label>
          <button disabled={floorBusy || !key} onClick={() => void saveFloor()}>Save Floor</button>
        </div>
        <p className="dcc-bridge-status">{floorStatus}</p>
      </section>

      <details className="dcc-bridge-panel">
        <summary>
          <strong>Obsidian GM Bridge</strong>
          <span>{key ? 'Configured' : 'Not configured'}</span>
        </summary>
        <div className="dcc-bridge-panel-body">
          <p className="dcc-bridge-help">GM only. The bridge secret must match the encrypted DCC_BRIDGE_SECRET configured in Cloudflare. Room ID: <code>{roomId}</code></p>
          {key
            ? <>
                <label className="dcc-bridge-key-label">Bridge secret
                  <input className="dcc-bridge-key" type="password" readOnly value={key} />
                </label>
                <div className="dcc-bridge-buttons">
                  <button onClick={copy}>Copy secret</button>
                  <button onClick={testServer}>Test bridge</button>
                  <button onClick={testDice}>Test Dice+</button>
                  <button onClick={clear}>Clear key</button>
                </div>
              </>
            : <button onClick={generate}>Generate bridge secret</button>}
          <p className="dcc-bridge-status">{status}</p>
        </div>
      </details>
    </>
  );
}
